import { Column, Icon, Table, Tooltip } from "@uigovpe/components";
import type { PendingProductAction, Product, ProductStatus } from "../types";
import { Routes } from "@/constants/routes";
import { useNavigate } from "react-router-dom";

const statusStyle: Record<
  ProductStatus,
  { label: string; container: string; marker: string }
> = {
  published: {
    label: "Publicado",
    container: "bg-[#e5f8d6]! text-zinc-800!",
    marker: "bg-[#86db64]!",
  },
  draft: {
    label: "Rascunho",
    container: "bg-[#e4f2ff]! text-zinc-800!",
    marker: "bg-[#3b82f6]!",
  },
  hidden: {
    label: "Oculto",
    container: "bg-zinc-200! text-zinc-800!",
    marker: "bg-zinc-300!",
  },
};

const ROWS_PER_PAGE = 4;

type Props = {
  first: number;
  isLoading: boolean;
  products: Product[];
  onAction: (action: PendingProductAction) => void;
  onPageChange: (first: number) => void;
};

export default function ProductTable({
  first,
  isLoading,
  products,
  onAction,
  onPageChange,
}: Props) {
  const navigate = useNavigate();
  const keywordTemplate = (product: Product, variant = "desktop") => {
    const id = `product-keyword-${variant}-${product.id}`;
    const target = `#${id}`;
    return (
      <>
        <span
          id={id}
          className="inline-block! max-w-36! truncate! rounded-full! bg-zinc-200! px-3! py-1! text-xs! text-zinc-600!"
        >
          {product.keywords[0] || "—"}
        </span>
        {product.keywords.length > 1 && (
          <Tooltip
            target={target}
            theme="light"
            position="top"
            content={<span>{product.keyword}</span>}
          />
        )}
      </>
    );
  };

  const statusTemplate = (product: Product) => {
    const style = statusStyle[product.status];
    return (
      <span
        className={`inline-flex! items-center! gap-2! rounded-full! px-3! py-1! text-sm! ${style.container}`}
      >
        <span className={`size-3! rounded-full! ${style.marker}`} />
        {style.label}
      </span>
    );
  };

  const actionsTemplate = (product: Product, variant = "desktop") => {
    const actions = [
      {
        icon: "visibility" as const,
        label: "Visualizar produto",
        disabled: false,
        run: () =>
          window.open(
            `https://empreende-mulher-vitrine.netlify.app/negocios/${product.id}`,
            "_blank",
            "noopener,noreferrer",
          ),
      },
      {
        icon: "edit" as const,
        label: "Editar produto",
        disabled: false,
        run: () => navigate(Routes.EDIT_PRODUCT(product.id)),
      },
      {
        icon: "block" as const,
        label:
          product.status === "hidden"
            ? "Produto já está oculto"
            : "Ocultar produto",
        disabled: product.status === "hidden",
        run: () => onAction({ type: "hide", product }),
      },
      {
        icon: "check_circle" as const,
        label:
          product.status === "published"
            ? "Produto já está publicado"
            : "Publicar na vitrine",
        disabled: product.status === "published",
        run: () => onAction({ type: "publish", product }),
      },
      {
        icon: "delete" as const,
        label: "Apagar produto",
        disabled: false,
        run: () => onAction({ type: "delete", product }),
      },
    ];
    return (
      <div className="flex! items-center! justify-end! gap-2! text-[#4e3873]!">
        {actions.map((action) => {
          const id = `product-${variant}-${action.icon}-${product.id}`;
          return (
            <span key={action.icon} id={id} className="inline-flex!">
              <button
                type="button"
                aria-label={action.label}
                disabled={action.disabled}
                onClick={action.run}
                className="cursor-pointer! rounded-sm! border-0! bg-transparent! p-0! text-[#4e3873]! transition! hover:opacity-75! disabled:cursor-not-allowed! disabled:opacity-40!"
              >
                <Icon icon={action.icon} outline className="text-[19px]!" />
              </button>
              <Tooltip
                target={`#${id}`}
                theme="light"
                position="top"
                content={<span>{action.label}</span>}
              />
            </span>
          );
        })}
      </div>
    );
  };

  const mobileProducts = products.slice(first, first + ROWS_PER_PAGE);
  const totalPages = Math.max(1, Math.ceil(products.length / ROWS_PER_PAGE));
  const currentPage = Math.min(
    Math.floor(first / ROWS_PER_PAGE) + 1,
    totalPages,
  );

  return (
    <div className="w-full! min-w-0!">
      <div className="project-table hidden! w-full! min-w-0! md:block! [&_.p-datatable-table]:min-w-full! [&_.p-datatable-table]:table-fixed!">
        <Table
          value={products}
          loading={isLoading}
          emptyMessage={
            <span className="block! w-full! text-center!">
              {isLoading
                ? "Carregando produtos..."
                : "Nenhum produto encontrado."}
            </span>
          }
          paginator
          first={first}
          rows={ROWS_PER_PAGE}
          pageLinkSize={5}
          paginatorTemplate="PrevPageLink PageLinks NextPageLink"
          showRowsPerPage={false}
          showCurrentPageReport={false}
          onPage={(event) => onPageChange(event.first)}
        >
          <Column field="name" header="Produto" className="w-[18%]!" />
          <Column
            header="Palavra-Chave"
            body={(product: Product) => keywordTemplate(product)}
            className="w-[16%]!"
          />
          <Column
            field="category"
            header="Categoria"
            className="w-[16%]!"
          />
          <Column
            header="Visibilidade"
            body={statusTemplate}
            className="w-[15%]!"
          />
          <Column
            field="updatedAt"
            header="Última atualização"
            className="w-[20%]!"
          />
          <Column
            header="Ações"
            body={(product: Product) => actionsTemplate(product)}
            className="w-[15%]!"
          />
        </Table>
      </div>

      <div className="flex! flex-col! gap-3! md:hidden!">
        {isLoading ? (
          <p className="py-8! text-center! text-sm! text-[#6b7280]!">
            Carregando produtos...
          </p>
        ) : mobileProducts.length === 0 ? (
          <p className="py-8! text-center! text-sm! text-[#6b7280]!">
            Nenhum produto encontrado.
          </p>
        ) : (
          mobileProducts.map((product) => (
            <article
              key={product.id}
              className="rounded-xl! border! border-[#e5e7eb]! bg-white! p-4! shadow-sm!"
            >
              <div className="flex! items-start! justify-between! gap-3!">
                <h3 className="m-0! min-w-0! text-base! leading-6! font-semibold! text-[#28272c]!">
                  {product.name}
                </h3>
                <span className="shrink-0!">{statusTemplate(product)}</span>
              </div>
              <dl className="my-4! grid! grid-cols-[auto_minmax(0,1fr)]! gap-x-3! gap-y-2! text-sm!">
                <dt className="font-medium! text-[#6b7280]!">Categoria</dt>
                <dd className="m-0! min-w-0! text-right! text-[#28272c]!">
                  {product.category || "—"}
                </dd>
                <dt className="font-medium! text-[#6b7280]!">Palavra-chave</dt>
                <dd className="m-0! min-w-0! text-right!">
                  {keywordTemplate(product, "mobile")}
                </dd>
                <dt className="font-medium! text-[#6b7280]!">Atualizado em</dt>
                <dd className="m-0! text-right! text-[#28272c]!">
                  {product.updatedAt}
                </dd>
              </dl>
              <div className="border-t! border-[#e5e7eb]! pt-3!">
                {actionsTemplate(product, "mobile")}
              </div>
            </article>
          ))
        )}

        {products.length > ROWS_PER_PAGE && (
          <nav
            className="mt-3! flex! items-center! justify-center! gap-4!"
            aria-label="Paginação de produtos"
          >
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => onPageChange(Math.max(0, first - ROWS_PER_PAGE))}
              className="flex! h-9! w-9! items-center! justify-center! rounded-full! border-0! bg-transparent! text-[#4e3873]! disabled:opacity-40!"
              aria-label="Página anterior"
            >
              <Icon icon="chevron_left" />
            </button>
            <span className="text-sm! text-[#494c57]!">
              {currentPage} de {totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(first + ROWS_PER_PAGE)}
              className="flex! h-9! w-9! items-center! justify-center! rounded-full! border-0! bg-transparent! text-[#4e3873]! disabled:opacity-40!"
              aria-label="Próxima página"
            >
              <Icon icon="chevron_right" />
            </button>
          </nav>
        )}
      </div>
    </div>
  );
}
